import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { RECRUITERS, getRecruiter } from "../lib/recruiters";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function RecruiterDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const r = getRecruiter(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!r) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Recruiter</div>
          <h1 className="ch-name">Recruiter not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.recruiters} style={{ color: "var(--gold-400)" }}>← Back to recruiters</Link>
          </p>
        </div>
      </section>
    );
  }

  const others = RECRUITERS.filter((x) => x.slug !== r.slug).slice(0, 3);
  const totalOffers = r.history.reduce((s, h) => s + h.offers, 0);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`RECRUITER · ${r.sector.toUpperCase()}`}
        title={r.name}
        back={{ label: "All recruiters", to: ROUTES.recruiters }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.recruiters}>Recruiters</Link>
            <span aria-hidden>/</span>
            <span>{r.sector}</span>
          </nav>
          <div className="ch-eyebrow">{r.tier}</div>
          <span className="ch-short">{r.offers}</span>
          <h1 className="ch-name">{r.name}</h1>
          <p className="ch-summary">{r.intro[0]}</p>

          <div className="ch-stats">
            <div>
              <div className="num">{totalOffers}</div>
              <div className="lbl">Cumulative offers · 5y</div>
            </div>
            <div>
              <div className="num">{r.history.length}</div>
              <div className="lbl">Drives · 5y</div>
            </div>
            <div>
              <div className="num">{r.roles.length}</div>
              <div className="lbl">Active roles</div>
            </div>
            <div>
              <div className="num">{r.alumni.length}+</div>
              <div className="lbl">BIET alumni at company</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>About the recruiter</h2>
              {r.intro.slice(1).map((p, i) => <p key={i}>{p}</p>)}
              {r.intro.length === 1 && <p>{r.intro[0]}</p>}

              <h2>Hiring history at BIET</h2>
              <table className="center-projects">
                <thead>
                  <tr>
                    <th>Cycle</th>
                    <th style={{ textAlign: "right" }}>Offers</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {r.history.map((h, i) => (
                    <tr key={i}>
                      <td className="tt">{h.cycle}</td>
                      <td className="amt" style={{ textAlign: "right" }}>{h.offers}</td>
                      <td>{h.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2>Roles offered</h2>
              <table className="center-projects">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Eligible branches</th>
                    <th>CTC</th>
                    <th>Format</th>
                  </tr>
                </thead>
                <tbody>
                  {r.roles.map((rl, i) => (
                    <tr key={i}>
                      <td className="tt">{rl.title}</td>
                      <td>{rl.branches.join(" · ")}</td>
                      <td className="amt">{rl.ctc}</td>
                      <td>{rl.format}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2>Selection process</h2>
              <ol className="event-schedule">
                {r.process.map((s, i) => (
                  <li key={i}>
                    <span className="es-time">Step {i + 1}</span>
                    <span className="es-line" aria-hidden />
                    <span className="es-body">
                      <span className="es-what">{s}</span>
                    </span>
                  </li>
                ))}
              </ol>

              <h2>BIET alumni at {r.name}</h2>
              <div className="center-faculty">
                {r.alumni.map((a, i) => (
                  <div key={i} className="cl-cell" style={{ border: "1px solid var(--rule)" }}>
                    <div className="cl-role">{a.batch} · {a.city}</div>
                    <div className="cl-name">{a.name}</div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                      {a.role}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Sector</div>
                <div className="dmc-value">{r.sector}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Tier</div>
                <div className="dmc-value">{r.tier}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Last drive</div>
                <div className="dmc-value">{r.history[0]?.cycle}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">T&P contact</div>
                <div className="dmc-value"><a href={`mailto:${r.contact.tnpEmail}`}>{r.contact.tnpEmail}</a></div>
              </div>
              {r.contact.recruiterPortal && (
                <div className="dmc-block">
                  <div className="dmc-label">Recruiter portal</div>
                  <div className="dmc-value"><a href={r.contact.recruiterPortal} target="_blank" rel="noreferrer">{r.contact.recruiterPortal}</a></div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More recruiters</div>
              <h2>Other companies hiring at BIET</h2>
            </div>
            <div className="er-grid">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.recruiters}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{o.history[0]?.offers ?? "—"}</span>
                    <span className="er-month">OFFERS</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.sector}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.tier}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
